import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import StatutoryBooks from '../Bookkeeping/StatutoryBooks';

// ── SLF™ Accounting — the statutory books ───────────────────────────────────────────────────
// Its own module rather than a view inside the invoicing app, for three reasons that are not about
// tidiness:
//
//   · It is a different job. Typing an invoice's line items and closing a company's accounting year
//     are done by different people, and closing cannot be undone.
//   · It is a different permission ('accounting_books'). Closing a period, editing a chart of
//     accounts and stamping vouchers with a five-year destruction lock should not ride along with
//     the right to enter invoice lines.
//   · It is a different audience. These are the books a DBD inspector asks to see.
//
// Its client list is also its own: routes/bookkeeping.ts hides any client with no scanned invoice,
// which would make a newly incorporated company — the one that most needs its books opened —
// unreachable here.

type Client = {
  id: string; name: string; client_no: string; entity_type: string; has_vat: number;
  periods: number; last_status: string; last_period: string; accounts: number; drafts: number; entries: number;
};

const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const range = (s: string) => String(s || '').split(' → ').map(dmy).join(' – ');

const pill: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 800, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap',
};

export default function AccountingBooks() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [clients, setClients] = useState<Client[] | null>(null);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Client | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    const h = setTimeout(() => {
      api.acctClients(q)
        .then((d: any) => { if (alive) setClients(d.clients || []); })
        .catch((e: any) => { if (alive) { setClients([]); setErr(e.message); } });
    }, q ? 220 : 0);
    return () => { alive = false; clearTimeout(h); };
  }, [q]);

  // The chosen client rides in the URL, so a refresh — and a link to a colleague — lands on the
  // same client's books rather than back at the picker.
  useEffect(() => {
    const id = params.get('client');
    if (!id || picked?.id === id || !clients) return;
    const c = clients.find(x => x.id === id);
    if (c) setPicked(c);
  }, [params, clients, picked]);

  const pick = (c: Client | null) => {
    setPicked(c);
    setParams(p => { const n = new URLSearchParams(p); if (c) n.set('client', c.id); else n.delete('client'); return n; }, { replace: true });
  };

  if (picked) {
    return (
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '20px 16px', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <button onClick={() => pick(null)} style={{ padding: '7px 12px', fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer' }}>
            <i className="ti ti-arrow-left" aria-hidden /> {t('pa_books_all_clients')}
          </button>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{picked.name}</div>
          {picked.client_no && <span style={{ ...pill, color: 'var(--text-tertiary)', background: 'var(--bg-app)', border: '1px solid var(--border)' }}>{picked.client_no}</span>}
        </div>
        <StatutoryBooks clientId={picked.id} clientName={picked.name} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 16px', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('pa_books_title')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{clients?.length ?? ''}</div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 12, lineHeight: 1.6 }}>
        {t('pa_books_intro')}
      </div>
      <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('pa_books_search_ph')}
        style={{ width: '100%', padding: '11px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box', marginBottom: 12 }} />

      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>{err}</div>}

      {clients === null ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
      ) : !clients.length ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('pa_books_empty')}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 9 }}>
          {clients.map(c => (
            <button key={c.id} onClick={() => pick(c)}
              style={{ textAlign: 'left', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'var(--bg-surface)', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                <div style={{ flex: 1 }} />
                {/* A waiting draft is the one thing on this card somebody has to act on. */}
                {!!c.drafts && <span style={{ ...pill, color: '#92400E', background: '#FFFBEB', border: '1px solid #F1C232' }}>{t('pa_books_drafts_pill', { n: c.drafts })}</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4, lineHeight: 1.7 }}>
                {c.periods === 0 ? (
                  <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{t('pa_books_no_period')}</span>
                ) : (
                  <>
                    {range(c.last_period)}
                    {c.last_status === 'closed'
                      ? <span style={{ ...pill, marginLeft: 6, color: '#166534', background: '#E8F6EE', border: '1px solid #BBE5CB' }}>{t('pa_books_pill_closed')}</span>
                      : <span style={{ ...pill, marginLeft: 6, color: '#1D4ED8', background: '#EFF6FF', border: '1px solid #BFDBFE' }}>{t('pa_books_pill_open')}</span>}
                  </>
                )}
                <br />
                {t('pa_books_card_counts', { accounts: c.accounts, entries: c.entries })}{c.has_vat ? ` · ${t('pa_books_card_vat')}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
